import React, { useRef, useEffect } from 'react';
import { Button, Dropdown, Space, Tag } from 'antd';
import {
  SettingOutlined,
  MoreOutlined,
  DeleteOutlined,
  CopyOutlined,
  PlusOutlined,
  HolderOutlined,
} from '@ant-design/icons';

const WorkflowStepRow = ({
  item,
  stepNumber,
  selected,
  onSelect,
  onDelete,
  onDisconnectEdge,
  onDuplicate,
  onInsertAfter,
  // Drag state passed from parent (WorkflowListView)
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging,       // this specific row is being dragged
  showPlaceholder,  // 'above' | 'below' | null
  isMatched,
  isCurrentMatch,
  fade,
}) => {
  const { node, meta } = item;
  const Icon = meta?.icon;
  const title = meta?.listLabel?.(node?.config) || meta?.label;
  const subtitle = meta?.subtitle;
  const nodeLabel = node?.config?._label;
  const isTrigger = node?.type?.startsWith('trigger.');
  const rowRef = useRef(null);

  useEffect(() => {
    if ((selected || isCurrentMatch) && rowRef.current) {
      const scrollTimer = setTimeout(() => {
        const container = rowRef.current?.closest('.flow-builder-workflow-scroll');
        if (container && rowRef.current) {
          const containerRect = container.getBoundingClientRect();
          const elemRect = rowRef.current.getBoundingClientRect();
          if (containerRect.height > 0) {
            const offset = elemRect.top - containerRect.top;
            const targetScrollTop = container.scrollTop + offset - (containerRect.height / 2) + (elemRect.height / 2);
            container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
          }
        }
      }, 100);
      return () => clearTimeout(scrollTimer);
    }
  }, [selected, isCurrentMatch]);

  // Back-refs get a lighter menu — only disconnect this link, not delete the real node
  const menuItems = item.isBackRef
    ? [
        { key: 'config', label: 'Configure', icon: <SettingOutlined /> },
        { type: 'divider' },
        {
          key: 'disconnect',
          label: 'Remove this reference',
          icon: <DeleteOutlined />,
          danger: true,
        },
      ]
    : [
        { key: 'config', label: 'Configure', icon: <SettingOutlined /> },
        { key: 'insert', label: 'Insert step below', icon: <PlusOutlined /> },
        { key: 'duplicate', label: 'Duplicate', icon: <CopyOutlined /> },
        { type: 'divider' },
        { key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true },
      ];

  const onMenu = ({ key, domEvent }) => {
    // Prevent the menu item click from bubbling through the React portal
    // tree to the card's onClick (which would open the config drawer).
    domEvent?.stopPropagation();
    if (key === 'config') onSelect?.(node.id);
    if (key === 'insert') onInsertAfter?.(node.id);
    if (key === 'delete') onDelete?.(node.id);
    if (key === 'duplicate') onDuplicate?.(node);
    // Back-ref: remove only the specific incoming edge (parentNodeId → node.id)
    if (key === 'disconnect') onDisconnectEdge?.(item.parentNodeId, node.id);
  };

  const cardStyle = {
    borderLeftColor: meta?.color,
    opacity: fade ? 0.35 : 1,
    boxShadow: isCurrentMatch
      ? '0 0 0 2px #faad14, 0 4px 12px rgba(250, 173, 20, 0.25)'
      : isMatched
        ? '0 0 0 1.5px #ffe58f, 0 2px 8px rgba(255, 229, 143, 0.15)'
        : undefined,
    borderWidth: isCurrentMatch || isMatched ? '1.5px' : '1px',
    borderColor: isCurrentMatch ? '#faad14' : isMatched ? '#ffe58f' : undefined,
    transition: 'box-shadow 0.2s ease',
  };

  // Placeholder: an animated gap div shown above or below this row
  const placeholder = (pos) => (
    <div
      key={`placeholder-${pos}`}
      className={`flow-step-placeholder flow-step-placeholder--visible`}
    />
  );

  return (
    <div ref={rowRef}>
      {/* Animated placeholder ABOVE */}
      {showPlaceholder === 'above' && placeholder('above')}

      <div
        className="flow-step-row"
        style={{ position: 'relative' }}
        onDragOver={(e) => {
          e.preventDefault();
          const rect = e.currentTarget.getBoundingClientRect();
          const pos = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
          onDragOver?.(node.id, pos);
        }}
        onDrop={(e) => { e.preventDefault(); onDrop?.(node.id); }}
      >
        <div className="flow-step-row__rail">
          <span className="flow-step-row__number">{stepNumber}</span>
          <div className="flow-step-row__line" />
        </div>
        <div
          className={`flow-step-card ${selected ? 'flow-step-card--selected' : ''}`}
          style={cardStyle}
          onClick={() => onSelect?.(node.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onSelect?.(node.id)}
        >
          <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space align="start">
              {/* Drag handle — only non-triggers
              {!isTrigger && (
                <div
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', node.id);
                    // Call SYNCHRONOUSLY so draggingRef.current is set immediately.
                    // Browser captures the ghost image AT the dragstart event moment,
                    // so we fade the card only AFTER via requestAnimationFrame.
                    onDragStart?.(node.id);
                    requestAnimationFrame(() => {
                      if (rowRef.current) rowRef.current.style.opacity = '0.25';
                    });
                  }}
                  onDragEnd={() => {
                    // Restore opacity immediately via DOM (no re-render delay)
                    if (rowRef.current) rowRef.current.style.opacity = '';
                    onDragEnd?.();
                  }}
                  className="flow-step-card__drag-handle"
                  title="Drag to reorder"
                  onClick={(e) => e.stopPropagation()}
                >
                  <HolderOutlined />
                </div>
              )}
              */}
              <div
                className="flow-step-card__icon"
                style={{ background: meta?.color || '#8c8c8c' }}
              >
                {Icon && <Icon />}
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>{nodeLabel || title}</div>
                {nodeLabel && (
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 1 }}>{title}</div>
                )}
                {subtitle && !nodeLabel && (
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>{subtitle}</div>
                )}
                {item.orphan && <Tag color="warning">Not connected</Tag>}
                {!item.orphan && item.incomingNodes?.length > 0 && (
                  <Tag color="processing" style={{ marginTop: 4 }}>
                    Also used by: {item.incomingNodes.join(', ')}
                  </Tag>
                )}
                {item.isBackRef && (
                  <Tag style={{ marginTop: 4, borderStyle: 'dashed', color: '#722ed1', borderColor: '#d3adf7', background: '#f9f0ff' }}>
                    ↩ Also used here
                  </Tag>
                )}
              </div>
            </Space>
            <Dropdown menu={{ items: menuItems, onClick: onMenu }} trigger={['click']}>
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </Space>
        </div>
      </div>

      {/* Animated placeholder BELOW */}
      {showPlaceholder === 'below' && placeholder('below')}
    </div>
  );
};

export default WorkflowStepRow;
